import React from "react";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";
import Img from "gatsby-image";

import Content from "../components/Content";
import useWelsh, { useWelshMap } from "../useWelsh";
import * as classes from "./Pages.module.css";

import newsletter from "../documents/Community newsletter - June 2021.pdf";
import briefing from "../documents/Stakeholder briefing - March 2021.pdf";
import leaflet from "../documents/Preliminary engagement - Feb 2021.pdf";

const map = {
  english: {
    title: "The Process",
    overview: {
      title: "Overview",
      text: `Solar projects in Wales which are above 10MW of power output are classed as Developments of National Significance (DNS). Unlike planning applications which are determined by the Local Planning Authority, all applications for DNS projects are instead made directly to the Welsh Government. This process is managed by the Planning Inspectorate Wales who has set out clear guidance for the procedures a DNS project must follow. Decisions are made in the context of the Welsh Government’s national planning policy and the local authority’s Local Development Plan.
      A flowchart of the DNS process is provided below:`,
    },
    engagement: {
      title: "Early engagement",
      text1: `Following early engagement with Monmouthshire County Council and other groups, we submitted a Screening Request to the Welsh Government to request an opinion on whether the project.
      On 21 December 2020, we gave formal notification to the Welsh Ministers of its intention to make an application for DNS under section 62D of the Town and Country Planning Act.
      On 26 January 2021, the Planning Inspectorate published their Notice of Acceptance which sets out a 12 month deadline for us to carry out further statutory consultation and formally submit the application to the Planning Inspectorate.
      In February 2021, we undertook preliminary engagement with the community to obtain initial feedback on the proposed project and to provide an overview of the process.  As part of this engagement, we mailed over 320 residences in the Llanover Community providing initial information on our proposal and inviting any feedback.`,
      leaflet: "Information leaflet – February 2021",
      text2: `We also held community stakeholder briefings to provide information on our proposals and the process we are taking as we move towards submission of our application. Information that we shared is provided in the links below:`,
      briefing: "Stakeholder briefing – March 2021",
      text3:
        "We have reflected on the feedback we have received from local residents and stakeholders and, where possible, have amended the proposal in response to the comments received.",
    },
    consultation: {
      title: "Statutory pre-application consultation",
      text1: `It is a requirement of the DNS process that applicants must consult on their plans for a period of 42 days.  This is to provide technical and community stakeholders, and local people, an opportunity to provide feedback on the project and the assessments produced in support of our plans.
      In support of this consultation, we have written to specific community consultees, including representatives from Monmouthshire County Council and Llanover Community Council, to inform them of our proposals and directing them to the documents uploaded on this website.
      In addition, we have sent a community newsletter to over 320 homes in the local area to inform people of our consultation and directing them to the website to allow them to view our proposals and provide feedback.`,
      newsletter: "Community newsletter – June 2021",
      text2: `We will also be advertising this consultation in the Abergavenny Chronicle and placing notices around the site to ensure people in close proximity are informed.`,
    },
    events: {
      title: "Consultation events",
      text1:
        "Due to the Covid-19 pandemic, plans for undertaking community consultation are different to what we would usually propose.  While this means we will not have the opportunity to undertake face-to-face events, we will be undertaking two virtual events where we will present our proposals and invite questions from local people.  These will take place as follows:",
      link1: "Wednesday 28th July at 7pm",
      link2: "Thursday 5th August at 1pm",
      text2: "You can register for either event at the links above.",
    },
    submission: {
      title: "Submission and determination",
      text: `Once our formal consultation has been completed, Great House Energy Centre will be undertaking a period of review to reflect on all the feedback received from all consultees during the public consultation process.  All feedback will be recorded and we will provide specific details of how we intend to respond to each point in turn.
      The final project designs along with all the application documentation will then be submitted to the Planning Inspectorate.  Once submitted, the Planning Inspectorate has a period of 28 days to decide whether the application is valid and contains the minimum general requirements that a DNS application must consist of.
      The determination period for the Welsh Ministers to make a decision on a DNS application is 36 weeks from the date of acceptance of the valid application.`,
    },
  },
  welsh: {
    title: "Y Broses",
    text: `Mae prosiectau solar yng Nghymru sy'n cynhyrchu allbwn pŵer mwy na 10MW yn cael eu hystyried yn Ddatblygiadau o Arwyddocâd Cenedlaethol (DAC). Yn wahanol i geisiadau cynllunio arferol a bennir gan yr Awdurdod Cynllunio Lleol, caiff pob cais am brosiectau DAC eu gwneud yn uniongyrchol i Lywodraeth Cymru. Rheolir y broses hon gan Arolygiaeth Gynllunio Cymru sydd wedi darparu canllawiau clir ar gyfer y gweithdrefnau y mae'n rhaid i brosiect DAC eu dilyn. Gwneir penderfyniadau yng nghyd-destun polisi cynllunio cenedlaethol Llywodraeth Cymru a Chynllun Datblygu Lleol yr awdurdod lleol.
    Ceir siart llif o'r broses DAC isod:`,
    overview: {
      title: "Cynnydd a wnaed hyd yma",
      text: `Ar ôl ymgysylltu'n gynnar â Chyngor Sir Fynwy a grwpiau eraill, cyflwynwyd Cais Sgrinio i Lywodraeth Cymru i ofyn am farn ynglŷn â’r prosiect.
      Ar 21 Rhagfyr 2020, rhoesom hysbysiad ffurfiol i Weinidogion Cymru o'r bwriad i wneud cais am DAC o dan adran 62D o'r Ddeddf Cynllunio Gwlad a Thref.
      Ar 26 Ionawr 2021, cyhoeddodd yr Arolygiaeth Gynllunio ei Hysbysiad Derbyn sy'n nodi dyddiad cau o 12 mis i ni gynnal ymgynghoriad statudol pellach a chyflwyno'r cais yn ffurfiol i'r Arolygiaeth Gynllunio.`,
    },
    consultation: {
      title: "Ymgynghoriad cyhoeddus",
      text1: `Due to the Covid-19 pandemic, plans for undertaking community consultation are different to what we would usually propose. While this means we will not have the opportunity to undertake face-to-face events, we will provide numerous other opportunities for people to review our proposals and to get in touch with us.
      In February 2021, we mailed over 320 residences in the Llanover Community providing initial information on our proposal and inviting any feedback. The information leaflet we sent to residents is provided |here|.
      We also held briefings with David TC Davies MP and Llanover Community Council to provide information on our proposals and the process we are taking as we move towards submission of our application. Information that we shared is provided in the links below:`,
      presentation:
        "Presentation provided to David TC Davies MP on 5th March 2021",
      text2: `We have reflected on the feedback we have received from local residents and stakeholders and, where possible, have amended the proposal in response to the comments received. We are now updating our assessments to reflect these changes in advance of formal public consultation, which will likely take place in the late Spring / early Summer.
      Consultation will take place over an 8 week period, 2 weeks longer than is required by the Welsh Planning Inspectorate. This period will be initiated by sending further mail-outs to local residences providing more details on our proposals. We will also be hosting online public events and undertaking further virtual meetings with stakeholders to outline how our plans have developed since we previous engaged.
      If you wish to receive notifications on the timeline and our consultation, please register your interest |here|.`,
    },
    submission: {
      title: "Cyflwyno a phenderfynu",
      text: `Unwaith y bydd y cais llawn wedi'i gyflwyno, mae gan yr Arolygiaeth Gynllunio gyfnod o *28 diwrnod* i benderfynu a yw'r cais yn ddilys ac mae'n cynnwys y gofynion cyffredinol sylfaenol y mae'n rhaid i gais DAC eu cynnwys.
      Y cyfnod penderfynu i Weinidogion Cymru wneud penderfyniad ar gais DAC yw *36 wythnos o’r dyddiad y derbynnir y cais dilys*.`,
    },
  },
};

const Process = () => {
  const welsh = useWelsh();
  const text = useWelshMap(map);
  const { process } = useStaticQuery(graphql`
    query {
      process: file(relativePath: { eq: "process.png" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  return (
    <>
      <Helmet>
        <title>Penpergwm Solar Farm | The Process</title>
      </Helmet>
      <Content>
        <h1>{text.title}</h1>
        <h2>{text.overview.title}</h2>
        {text.overview.text.split("\n").map((words, i) => (
          <p key={i}>
            {words.split("*").map((x, j) => (j % 2 ? <b key={j}>{x}</b> : x))}
          </p>
        ))}
        <Img
          className={classes.grainyImage}
          fluid={process.childImageSharp.fluid}
          alt="Application flowchart"
        />
        <a
          className={classes.smallLink}
          href="https://gov.wales/sites/default/files/publications/2019-11/developments-of-national-significance-dns-procedural-guidance.pdf"
        >
          https://gov.wales/sites/default/files/publications/2019-11/developments-of-national-significance-dns-procedural-guidance.pdf
        </a>
        <h2>{text.engagement.title}</h2>
        {text.engagement.text1.split("\n").map((words, i) => (
          <p key={i}>{words}</p>
        ))}
        <a href={leaflet}>{text.engagement.leaflet}</a>
        {text.engagement.text2.split("\n").map((words, i) => (
          <p key={i}>{words}</p>
        ))}
        <a href={briefing}>{text.engagement.briefing}</a>
        {text.engagement.text3.split("\n").map((words, i) => (
          <p key={i}>{words}</p>
        ))}
        <h2>{text.consultation.title}</h2>
        {text.consultation.text1.split("\n").map((words, i) => (
          <p key={i}>{words}</p>
        ))}
        <a href={newsletter}>{text.consultation.newsletter}</a>
        {text.consultation.text2.split("\n").map((words, i) => (
          <p key={i}>
            {words.split("|").map((x, l) =>
              l % 2 ? (
                <a href={newsletter} key={l}>
                  {x}
                </a>
              ) : (
                x
              )
            )}
          </p>
        ))}
        <h2>{text.events.title}</h2>
        <p>{text.events.text1}</p>
        <ul>
          <li>
            <a href="https://us02web.zoom.us/webinar/register/WN_1ur9EsylRUm6-GsaT0EGvw">
              {text.events.link1}
            </a>
          </li>
          <li>
            <a href="https://us02web.zoom.us/webinar/register/WN_l5yMexLOQeqeA1B3V7IxAw">
              {text.events.link2}
            </a>
          </li>
        </ul>
        <p>{text.events.text2}</p>
        <h2>{text.submission.title}</h2>
        {text.submission.text.split("\n").map((words, i) => (
          <p key={i}>
            {words.split("*").map((x, j) => (j % 2 ? <b key={j}>{x}</b> : x))}
          </p>
        ))}
      </Content>
    </>
  );
};

export default Process;
